import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import { Popconfirm, Space, message } from "antd";
import {
  DISCOUNT_CODES_QUERY,
  useDiscountCodeDeleteMutation,
  useDiscountCodesQuery,
} from "@ticketingplatform/api/dist/discountCode";
import { DiscountCodeNode } from "@ticketingplatform/api/dist/discountCode/fragments/__generated__/discountCodeNode";
import {
  pageToSkip,
  renderTableAmountColumn,
  renderTableDateColumn,
} from "utils/tableUtils";
import { ColumnsType } from "antd/es/table";
import { DeleteIcon } from "assets/icons";
import { useVenueContext } from "contexts/VenueContext";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import {
  getColumnTextFilterProps,
  getTextFilterInput,
} from "components/data/TableTextFilter/utils";
import { Link, generatePath } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";
import { ROUTE_PATHS } from "const/paths";
import isPast from "date-fns/isPast";
import { DiscountCodesVariables } from "@ticketingplatform/api/dist/discountCode/__generated__/queries";

import { UseDiscountCodeListReturnType } from "./types";
import EventSummaryList from "./partials/EventSummaryList";

export const useDeleteDiscountCode = () => {
  const intl = useIntl();
  const [mutation, result] = useDiscountCodeDeleteMutation({
    evictQueries: [DISCOUNT_CODES_QUERY],
    onCompleted: () =>
      message.success(
        intl.formatMessage({
          defaultMessage: "Discount Code has been removed!",
        })
      ),
    onError: () =>
      message.error(
        intl.formatMessage({
          defaultMessage: "Cannot remove the Discount Code",
        })
      ),
  });

  const deleteDiscountCode = useCallback(
    (id: number) => mutation({ variables: { id } }),
    [mutation]
  );

  return [deleteDiscountCode, result] as const;
};

export const useDiscountCodeColumns = () => {
  const intl = useIntl();
  const [deleteDiscountCode] = useDeleteDiscountCode();
  const [{ sortOrder, filters }] = useTableQueryParams<DiscountCodeNode>();

  return useMemo<ColumnsType<DiscountCodeNode>>(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Discount Code",
        }),
        key: "code",
        dataIndex: "code",
        sorter: {
          multiple: 1,
        },
        sortOrder: sortOrder.code,
        ...getColumnTextFilterProps("code", filters?.code),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Discount Name",
        }),
        key: "name",
        dataIndex: "name",
        sorter: {
          multiple: 2,
        },
        sortOrder: sortOrder.name,
        ...getColumnTextFilterProps("name", filters?.name),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Discount",
        }),
        key: "amount",
        dataIndex: "amount",
        sorter: {
          multiple: 3,
        },
        sortOrder: sortOrder.amount,
        render: renderTableAmountColumn(),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Event",
        }),
        render(_, { isApplicableToAllEvents: isAll, events }) {
          const { nodes: items, count } = events || {};

          return <EventSummaryList {...{ items, count, isAll }} />;
        },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Created",
        }),
        key: "createdAt",
        dataIndex: "createdAt",
        sorter: {
          multiple: 4,
        },
        sortOrder: sortOrder.createdAt,
        render: renderTableDateColumn(),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Status",
        }),
        key: "expireAt",
        dataIndex: "expireAt",
        sorter: {
          multiple: 5,
        },
        sortOrder: sortOrder.expireAt,
        render(expireAt) {
          if (isPast(new Date(expireAt))) {
            return intl.formatMessage({ defaultMessage: "Inactive" });
          }

          return intl.formatMessage({ defaultMessage: "Active" });
        },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Action",
        }),
        key: "action",
        render: (_, { id, name }) => (
          <Space size="middle">
            <Link
              to={generatePath(ROUTE_PATHS.DiscountCodeEdit, {
                id: id.toString(),
              })}
            >
              <EditOutlined />
            </Link>
            <Popconfirm
              title={intl.formatMessage(
                {
                  defaultMessage:
                    "Are you sure you want to delete Discount Code: {name}?",
                },
                { name }
              )}
              onConfirm={() => deleteDiscountCode(id)}
            >
              <DeleteIcon />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [
      intl,
      sortOrder.code,
      sortOrder.name,
      sortOrder.amount,
      sortOrder.createdAt,
      sortOrder.expireAt,
      filters?.code,
      filters?.name,
      deleteDiscountCode,
    ]
  );
};

export const useDiscountCodeList = (): UseDiscountCodeListReturnType => {
  const { venue } = useVenueContext();
  const [{ page, pageSize, orderBy, filters }, setTableQueryParams] =
    useTableQueryParams<DiscountCodeNode>();

  const variables = useMemo<DiscountCodesVariables>(
    () => ({
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      filters: {
        ...(venue ? { venueId: { equals: venue.id } } : {}),
        ...getTextFilterInput(filters?.name, "name"),
        ...getTextFilterInput(filters?.code, "code"),
      },
      orderBy,
      eventsTake: 3,
    }),
    [filters?.code, filters?.name, orderBy, page, pageSize, venue]
  );

  const { data, loading } = useDiscountCodesQuery({ variables });

  const onChange = useCallback<UseDiscountCodeListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.discountCodes || {};

  return useMemo(
    () => ({
      loading,
      pagination: {
        pageSize,
        current: page,
        total,
      },
      dataSource,
      onChange,
    }),
    [dataSource, loading, onChange, page, pageSize, total]
  );
};
